// Revenue (contracts/revenue.md): environments and stores, subscription statuses and event kinds with their
// labels, tones and groups, money in major units with the USD estimate, and the player journey (paywall runs
// collapsed, one sentence per step). Shared by the Revenue, Customers, Events and player Subscription pages and
// by the mock (src/mocks/revenue.ts).

import { z } from "zod";
import { formatDateTime, formatShortDate } from "./dates";
import { currencyExponent } from "./payments";
import { PLAN_LABEL, PRODUCT_LABEL } from "./pro";

// ---------------------------------------------------------------- environments and stores (§1)

export const REVENUE_ENVS = ["production", "sandbox", "all"] as const;
export type RevenueEnvFilter = (typeof REVENUE_ENVS)[number];
export type RevenueEnv = Exclude<RevenueEnvFilter, "all">;
export const ENV_LABEL: Record<RevenueEnvFilter, string> = { production: "Production", sandbox: "Sandbox", all: "All" };

export const REVENUE_STORES = ["all", "appstore", "stripe"] as const;
export type RevenueStoreFilter = (typeof REVENUE_STORES)[number];
export type RevenueStore = Exclude<RevenueStoreFilter, "all">;
export const STORE_FILTER_LABEL: Record<RevenueStoreFilter, string> = { all: "All", appstore: "App Store", stripe: "Web (Stripe)" };
export const STORE_LABEL: Record<string, string> = { appstore: "App Store", stripe: "Stripe" };

/** "Production · every store", "Production + sandbox · Web (Stripe)": the scope in a page's description. */
export function scopeText(env: RevenueEnvFilter, store: RevenueStoreFilter): string {
  const e = env === "all" ? "Production + sandbox" : ENV_LABEL[env];
  return `${e} · ${store === "all" ? "every store" : STORE_FILTER_LABEL[store]}`;
}

export const isEnvFilter = (v: string | null | undefined): v is RevenueEnvFilter => (REVENUE_ENVS as readonly string[]).includes(v ?? "");
export const isStoreFilter = (v: string | null | undefined): v is RevenueStoreFilter => (REVENUE_STORES as readonly string[]).includes(v ?? "");

// ---------------------------------------------------------------- statuses (§3)

export const SUB_STATUSES = ["trial", "trial_cancelled", "active", "cancelled", "grace", "billing_retry", "expired", "refunded", "revoked", "lifetime"] as const;
export type SubStatus = (typeof SUB_STATUSES)[number];

export interface StatusMeta {
  label: string;
  tone: string;
  /** The subscription gives Pro right now. */
  access: boolean;
  hint: string;
}

const STATUS_META: Record<string, StatusMeta> = {
  trial: { label: "trial", tone: "violet", access: true, hint: "In the free trial; the store charges at the trial's end." },
  trial_cancelled: { label: "trial cancelled", tone: "warning", access: true, hint: "In the free trial with auto-renew off: it ends without a charge." },
  active: { label: "active", tone: "success", access: true, hint: "A paid period; renews at its end." },
  cancelled: { label: "cancelled", tone: "warning", access: true, hint: "A paid period with auto-renew off: Pro until it ends." },
  grace: { label: "grace", tone: "warning", access: true, hint: "The renewal failed; Pro continues through the store's billing grace period." },
  billing_retry: { label: "billing retry", tone: "danger", access: false, hint: "The renewal failed and the store keeps retrying (Apple: up to 60 days). No Pro meanwhile." },
  expired: { label: "expired", tone: "muted", access: false, hint: "Ended." },
  refunded: { label: "refunded", tone: "danger", access: false, hint: "Revoked by a refund or a dispute." },
  revoked: { label: "revoked", tone: "danger", access: false, hint: "Revoked otherwise (Family Sharing, an admin)." },
  lifetime: { label: "lifetime", tone: "accent", access: true, hint: "The lifetime purchase, owned." },
  free: { label: "free", tone: "muted", access: false, hint: "No store subscription in production." },
};

export const statusMeta = (status: string): StatusMeta => STATUS_META[status] ?? { label: status.replace(/_/g, " "), tone: "neutral", access: false, hint: "" };

/** summary.status: the best current status across production subscriptions (§4 players). */
export const STATUS_RANK = ["lifetime", "active", "grace", "trial", "cancelled", "trial_cancelled", "billing_retry", "expired", "refunded", "revoked"] as const;

export function bestStatus(statuses: readonly string[]): string {
  let best = "free";
  let rank = Infinity;
  for (const s of statuses) {
    const i = (STATUS_RANK as readonly string[]).indexOf(s);
    if (i >= 0 && i < rank) {
      rank = i;
      best = s;
    }
  }
  return best;
}

export const END_REASONS = ["trial_cancelled", "cancelled", "billing", "price_increase", "product_unavailable", "other"] as const;
export const END_REASON_LABEL: Record<string, string> = {
  trial_cancelled: "cancelled in the trial",
  cancelled: "cancelled",
  billing: "billing failed",
  price_increase: "price increase declined",
  product_unavailable: "product not for sale",
  other: "other",
};

/** The status chips on Customers, in this order; `active` also matches cancelled and grace on the API. */
export const STATUS_CHIPS: { value: SubStatus; label: string }[] = [
  { value: "trial", label: "Trial" },
  { value: "trial_cancelled", label: "Trial cancelled" },
  { value: "active", label: "Active" },
  { value: "cancelled", label: "Cancelled" },
  { value: "grace", label: "Grace" },
  { value: "billing_retry", label: "Billing retry" },
  { value: "expired", label: "Expired" },
  { value: "refunded", label: "Refunded" },
  { value: "revoked", label: "Revoked" },
  { value: "lifetime", label: "Lifetime" },
];

/** What `status=<s>` matches on the API: `active` also matches cancelled and grace. */
export const statusMatches = (filter: string, status: string) => filter === status || (filter === "active" && (status === "cancelled" || status === "grace"));

// ---------------------------------------------------------------- events (§3, §6)

export const EVENT_KINDS = [
  "trial_started",
  "subscribed",
  "resubscribed",
  "converted",
  "renewed",
  "recovered",
  "cancelled",
  "reactivated",
  "plan_changed",
  "billing_issue",
  "grace_expired",
  "expired",
  "refunded",
  "refund_declined",
  "refund_reversed",
  "refund_requested",
  "revoked",
  "lifetime_purchased",
  "price_increase",
  "renewal_extended",
  "offer_redeemed",
  "disputed",
  "dispute_closed",
  "other",
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

const KIND_META: Record<string, { label: string; tone: string }> = {
  trial_started: { label: "trial started", tone: "violet" },
  subscribed: { label: "subscribed", tone: "success" },
  resubscribed: { label: "resubscribed", tone: "success" },
  converted: { label: "converted", tone: "success" },
  renewed: { label: "renewed", tone: "success" },
  recovered: { label: "recovered", tone: "success" },
  cancelled: { label: "cancelled", tone: "warning" },
  reactivated: { label: "reactivated", tone: "info" },
  plan_changed: { label: "plan changed", tone: "info" },
  billing_issue: { label: "billing issue", tone: "danger" },
  grace_expired: { label: "grace expired", tone: "danger" },
  expired: { label: "expired", tone: "muted" },
  refunded: { label: "refunded", tone: "danger" },
  refund_declined: { label: "refund declined", tone: "muted" },
  refund_reversed: { label: "refund reversed", tone: "success" },
  refund_requested: { label: "refund requested", tone: "warning" },
  revoked: { label: "revoked", tone: "danger" },
  lifetime_purchased: { label: "lifetime bought", tone: "accent" },
  price_increase: { label: "price increase", tone: "warning" },
  renewal_extended: { label: "renewal extended", tone: "info" },
  offer_redeemed: { label: "offer redeemed", tone: "info" },
  disputed: { label: "disputed", tone: "danger" },
  dispute_closed: { label: "dispute closed", tone: "muted" },
  other: { label: "other", tone: "neutral" },
};

export const kindMeta = (kind: string) => KIND_META[kind] ?? { label: kind.replace(/_/g, " "), tone: "neutral" };

/** Kinds that move money: a charge, a refund (negative) or a reversed refund (positive again). */
export const CHARGE_KINDS = ["subscribed", "resubscribed", "converted", "renewed", "recovered", "lifetime_purchased", "refunded", "refund_reversed"] as const;
export const isChargeKind = (kind: string) => (CHARGE_KINDS as readonly string[]).includes(kind);

export const KIND_GROUPS = ["all", "charges", "trials", "churn"] as const;
export type KindGroup = (typeof KIND_GROUPS)[number];
export const KIND_GROUP_LABEL: Record<KindGroup, string> = { all: "All", charges: "Charges", trials: "Trials", churn: "Churn" };
/** The kinds each group asks for (`trials` also means `cancelled` only during the trial, which the API applies). */
export const GROUP_KINDS: Record<Exclude<KindGroup, "all">, readonly string[]> = {
  charges: CHARGE_KINDS,
  trials: ["trial_started", "converted", "cancelled"],
  churn: ["cancelled", "expired", "refunded", "revoked", "billing_issue"],
};

/** Does an event match `kind=<list>` (kinds and groups, comma-separated)? */
export function kindMatches(filter: string, e: { kind: string; detail: string | null }): boolean {
  return filter
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .some((k) => {
      if (k === "charges") return isChargeKind(e.kind);
      if (k === "trials") return e.kind === "trial_started" || e.kind === "converted" || (e.kind === "cancelled" && e.detail === "during_trial");
      if (k === "churn") return (GROUP_KINDS.churn as readonly string[]).includes(e.kind) && !(e.kind === "cancelled" && e.detail === "during_trial");
      return k === e.kind;
    });
}

// ---------------------------------------------------------------- triggers, plans, products, storefronts

export const TRIGGER_LABEL: Record<string, string> = {
  stats: "Stats",
  archive: "Archive",
  hardmode: "Hard Mode",
  freeze: "Freezes",
  levels: "Levels",
  badge: "Badges",
  settings: "Settings",
  onboarding: "Onboarding",
  app_open: "App open",
  exit_offer: "Exit offer",
};
export const triggerLabel = (t: string | null | undefined) => (t ? (TRIGGER_LABEL[t] ?? t.replace(/_/g, " ")) : "Unknown");

export const planLabel = (plan: string | null | undefined) =>
  plan && plan in PLAN_LABEL ? PLAN_LABEL[plan as keyof typeof PLAN_LABEL] : plan ? plan.replace(/_/g, " ") : "—";

/** "Yearly · $29.99" for the base products, "Yearly (iqdaily.pro.annual.offer)" for an alternative one. */
export function productLabel(productId: string | null | undefined, plan?: string | null): string {
  if (!productId) return planLabel(plan);
  if (PRODUCT_LABEL[productId]) return PRODUCT_LABEL[productId]!;
  return plan ? `${planLabel(plan)} · ${productId.replace(/^iqdaily\.pro\.[a-z]+\.?/, "") || productId}` : productId;
}

/** "Yearly" for a base product, "Yearly · offer" for an alternative one: for rows that show the customer's own price. */
export function productShort(productId: string | null | undefined, plan?: string | null): string {
  const label = planLabel(plan);
  if (!productId || (plan && productId === `iqdaily.pro.${plan}`)) return label;
  const suffix = productId.replace(/^iqdaily\.pro\.[a-z]+\.?/, "");
  return suffix && suffix !== productId ? `${label} · ${suffix}` : plan ? label : productId;
}

/** App Store storefronts (ISO 3166-1 alpha-3) the panel names; anything else shows its code. */
const ALPHA3: Record<string, string> = {
  USA: "US", GBR: "GB", DEU: "DE", FRA: "FR", NLD: "NL", AUS: "AU", CAN: "CA", JPN: "JP", BRA: "BR", IND: "IN", SWE: "SE", KOR: "KR", MEX: "MX",
  ESP: "ES", ITA: "IT", CHE: "CH", IRL: "IE", NZL: "NZ", NOR: "NO", DNK: "DK", POL: "PL", TUR: "TR", SGP: "SG", BGD: "BD",
};
let regionNames: Intl.DisplayNames | null = null;
/** "AUS" or "AU" → "Australia"; null when it isn't a known code. */
export function regionName(code: string | null | undefined): string | null {
  if (!code) return null;
  const alpha2 = code.length === 3 ? ALPHA3[code.toUpperCase()] : code.toUpperCase();
  if (!alpha2) return null;
  try {
    regionNames ??= new Intl.DisplayNames(["en"], { type: "region" });
    const name = regionNames.of(alpha2);
    return name && name !== alpha2 ? name : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------- money (§2)

export interface Money {
  amount: number;
  currency: string;
}

const usdFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const usdWholeFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** A USD estimate: "$29.99" (or "$1,240" without cents for big round numbers on an axis). */
export function fmtUsd(n: number | null | undefined, whole = false): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return (whole ? usdWholeFmt : usdFmt).format(n);
}

/** Major units in the currency's own decimals: 49.99 AUD → "A$49.99", 900 JPY → "¥900". */
export function fmtMoney(m: Money | null | undefined): string {
  if (!m) return "—";
  const exp = currencyExponent(m.currency);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: m.currency.toUpperCase(), minimumFractionDigits: exp, maximumFractionDigits: exp }).format(m.amount);
  } catch {
    return `${m.amount.toFixed(exp)} ${m.currency.toUpperCase()}`;
  }
}

/** "A$49.99 (≈ $29.99)"; a USD price is shown once. */
export function fmtMoneyUsd(price: Money | null | undefined, usd: number | null | undefined): string {
  if (!price) return usd === null || usd === undefined ? "—" : `≈ ${fmtUsd(usd)}`;
  if (price.currency.toUpperCase() === "USD" || usd === null || usd === undefined) return fmtMoney(price);
  return `${fmtMoney(price)} (≈ ${fmtUsd(usd)})`;
}

/** Net amounts per currency, largest first: "A$149.97 · $89.97". */
export const fmtMoneyList = (list: Money[]) => (list.length ? [...list].sort((a, b) => b.amount - a.amount).map(fmtMoney).join(" · ") : "—");

/** config `revenue.estimates` (§2), admin-editable; the proceeds estimate. */
export const RevenueEstimates = z.strictObject({
  appleCommission: z.number().min(0).max(0.5),
  stripeFeePercent: z.number().min(0).max(0.2),
  stripeFeeFixedUsd: z.number().min(0).max(5),
});
export type RevenueEstimates = z.infer<typeof RevenueEstimates>;
export const DEFAULT_ESTIMATES: RevenueEstimates = { appleCommission: 0.15, stripeFeePercent: 0.029, stripeFeeFixedUsd: 0.3 };
export const REVENUE_CONFIG = {
  "revenue.estimates": { schema: RevenueEstimates, default: DEFAULT_ESTIMATES },
} as const;

/** The estimated proceeds of one charge (negative for a refund): Apple keeps its commission, Stripe its fee. */
export function proceedsOf(store: string, usd: number, e: RevenueEstimates = DEFAULT_ESTIMATES): number {
  if (store === "stripe") return usd >= 0 ? usd * (1 - e.stripeFeePercent) - e.stripeFeeFixedUsd : usd;
  return usd * (1 - e.appleCommission);
}

// ---------------------------------------------------------------- journey (§4 players)

/** The parts of an Event a sentence needs. */
export interface JourneyEvent {
  kind: string;
  detail: string | null;
  plan: string | null;
  productId: string | null;
  price: Money | null;
  usd: number | null;
  periodEndsAt: string | null;
  environment: RevenueEnv;
  store: string;
  raw: string | null;
}

export interface JourneyInput {
  at: string;
  kind: string;
  event: JourneyEvent | null;
  trigger: string | null;
  variant: string | null;
  subscriptionId: string | null;
  price: Money | null;
  usd: number | null;
  reason: string | null;
  expiresAt: string | null;
  grantedBy: string | null;
}

export interface JourneyStep {
  key: string;
  at: string;
  kind: string;
  tone: string;
  title: string;
  /** A second, quieter line (the paywall it came from, the raw store type). */
  note: string | null;
  money: string | null;
  environment: RevenueEnv | null;
  store: string | null;
  /** The last step: an expected charge, not something that happened. */
  future: boolean;
  /** A collapsed run of paywall impressions. */
  paywalls: { at: string; trigger: string | null; variant: string | null }[] | null;
}

const shortDay = (iso: string | null | undefined) => (iso ? formatShortDate(iso.slice(0, 10)) : "?");
const priced = (e: { price: Money | null; usd: number | null }) => (e.price || e.usd !== null ? fmtMoneyUsd(e.price, e.usd) : null);

/** One human sentence for a store event. Sandbox periods last minutes, so their dates carry the time. */
export function eventSentence(e: JourneyEvent): string {
  const plan = planLabel(e.plan);
  const day = (iso: string | null | undefined) => (e.environment === "sandbox" && iso ? formatDateTime(iso) : shortDay(iso));
  switch (e.kind) {
    case "trial_started":
      return `Started the free trial of ${plan}${e.periodEndsAt ? `; it ends ${day(e.periodEndsAt)}` : ""}`;
    case "subscribed":
      return `Subscribed to ${plan}`;
    case "resubscribed":
      return `Subscribed to ${plan} again`;
    case "converted":
      return `The trial converted: first charge for ${plan}`;
    case "renewed":
      return `${plan} renewed${e.periodEndsAt ? ` until ${day(e.periodEndsAt)}` : ""}`;
    case "recovered":
      return "The payment went through after a billing issue";
    case "cancelled":
      return e.detail === "during_trial"
        ? `Turned off auto-renew during the trial: no charge, Pro until ${day(e.periodEndsAt)}`
        : `Cancelled: auto-renew off, Pro until ${day(e.periodEndsAt)}`;
    case "reactivated":
      return "Turned auto-renew back on";
    case "plan_changed":
      return e.detail === "upgrade" ? "Chose an upgrade at the next renewal" : e.detail === "downgrade" ? "Chose a downgrade at the next renewal" : "Undid a pending plan change";
    case "billing_issue":
      return e.detail === "grace_period" ? "The renewal failed: Pro continues in the billing grace period" : "The renewal failed: the store keeps retrying";
    case "grace_expired":
      return "The grace period ended without a payment";
    case "expired": {
      const why: Record<string, string> = {
        voluntary: "after the customer cancelled",
        billing_retry: "billing never recovered",
        price_increase: "a price increase wasn't accepted",
        product_not_for_sale: "the product is no longer for sale",
        cancelled: "after the customer cancelled",
      };
      return `The subscription ended${e.detail && why[e.detail] ? `: ${why[e.detail]}` : ""}`;
    }
    case "refunded":
      return e.store === "stripe" ? "Refunded through Stripe; Pro revoked" : "Apple refunded the purchase; Pro revoked";
    case "refund_declined":
      return "Apple declined the refund request";
    case "refund_reversed":
      return "The refund was reversed: charged again";
    case "refund_requested":
      return "Asked Apple for a refund";
    case "revoked":
      return "Access revoked (Family Sharing)";
    case "lifetime_purchased":
      return "Bought Lifetime";
    case "price_increase":
      return e.detail === "accepted" ? "Accepted a price increase" : "A price increase is pending the customer's consent";
    case "renewal_extended":
      return "The renewal date was extended";
    case "offer_redeemed":
      return "Redeemed an offer";
    case "disputed":
      return "The payment was disputed; Pro revoked";
    case "dispute_closed":
      return "The dispute was closed";
    default:
      return `Store notification ${e.detail ?? e.raw ?? e.kind}`;
  }
}

const sandboxOf = (s: JourneyInput) => s.event?.environment ?? null;

/**
 * The journey as steps, oldest first: consecutive paywall impressions collapse into one step, store events in
 * another environment are left out (`env` = production keeps production ones; paywalls, installs and grants
 * always show), and the next charge is the last step.
 */
export function journeySteps(
  journey: JourneyInput[],
  env: RevenueEnvFilter,
  subEnv: (subscriptionId: string | null) => RevenueEnv | null = () => null,
): JourneyStep[] {
  const out: JourneyStep[] = [];
  const keep = (e: RevenueEnv | null) => env === "all" || e === null || e === env;
  journey.forEach((s, i) => {
    const key = `${i}-${s.kind}-${s.at}`;
    if (s.kind === "paywall") {
      const last = out[out.length - 1];
      const item = { at: s.at, trigger: s.trigger, variant: s.variant };
      if (last?.paywalls) {
        last.paywalls.push(item);
        last.title = `Saw the paywall ${last.paywalls.length} times`;
        last.note = [...new Set(last.paywalls.map((p) => triggerLabel(p.trigger)))].join(" · ");
        return;
      }
      out.push({
        key,
        at: s.at,
        kind: "paywall",
        tone: "neutral",
        title: "Saw the paywall",
        note: `${triggerLabel(s.trigger)}${s.variant ? ` · variant ${s.variant.toUpperCase()}` : ""}`,
        money: null,
        environment: null,
        store: null,
        future: false,
        paywalls: [item],
      });
      return;
    }
    if (s.kind === "installed") {
      out.push({ key, at: s.at, kind: s.kind, tone: "info", title: "Installed the app", note: null, money: null, environment: null, store: null, future: false, paywalls: null });
      return;
    }
    if (s.kind === "grant" || s.kind === "grant_revoked") {
      const title = s.kind === "grant" ? `Support grant: Pro${s.expiresAt ? ` until ${shortDay(s.expiresAt)}` : ""}` : "The support grant was revoked";
      const note = [s.reason ? `“${s.reason}”` : null, s.grantedBy].filter(Boolean).join(" · ") || null;
      out.push({ key, at: s.at, kind: s.kind, tone: "violet", title, note, money: null, environment: null, store: null, future: false, paywalls: null });
      return;
    }
    if (s.kind === "next_charge") {
      const e = subEnv(s.subscriptionId);
      if (!keep(e)) return;
      out.push({
        key,
        at: s.at,
        kind: s.kind,
        tone: "accent",
        title: `Next charge on ${e === "sandbox" ? formatDateTime(s.at) : shortDay(s.at)}: ${fmtMoneyUsd(s.price, s.usd)}`,
        note: null,
        money: null,
        environment: e,
        store: null,
        future: true,
        paywalls: null,
      });
      return;
    }
    const ev = s.event;
    if (!ev) {
      out.push({ key, at: s.at, kind: s.kind, tone: kindMeta(s.kind).tone, title: kindMeta(s.kind).label, note: null, money: null, environment: null, store: null, future: false, paywalls: null });
      return;
    }
    if (!keep(sandboxOf(s))) return;
    const from = s.trigger ? `from the ${triggerLabel(s.trigger)} paywall${s.variant ? ` (variant ${s.variant.toUpperCase()})` : ""}` : null;
    out.push({
      key,
      at: s.at,
      kind: ev.kind,
      tone: kindMeta(ev.kind).tone,
      title: eventSentence(ev),
      note: [from, productShort(ev.productId, ev.plan), STORE_LABEL[ev.store] ?? ev.store].filter(Boolean).join(" · "),
      money: isChargeKind(ev.kind) ? priced(ev) : null,
      environment: ev.environment,
      store: ev.store,
      future: false,
      paywalls: null,
    });
  });
  return out;
}
